import axios from 'axios';
import { API_CONFIG, buildExternalAiUrl } from './apiConfig';
import { getExternalAiAuthorizationHeaders } from './externalAiAuthHeaders';

function externalAiHeaders() {
  const headers = {};
  if (API_CONFIG.API_KEY && API_CONFIG.API_SECRET) {
    headers['X-API-Key'] = API_CONFIG.API_KEY;
    headers['X-API-Secret'] = API_CONFIG.API_SECRET;
  }
  return { ...headers, ...getExternalAiAuthorizationHeaders() };
}

/**
 * Poll job status until completion, failure, or timeout
 * @param {string} jobId - Job ID to poll
 * @param {Function} onStatusChange - Callback for status updates
 * @returns {Promise} - Resolves with final result or rejects with error
 */
export const pollJobStatus = (jobId, onStatusChange = null) => {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    let pollInterval = null;
    const startTime = Date.now();

    const checkStatus = async () => {
      attempts++;
      const elapsedTime = Date.now() - startTime;

      // Check timeout
      if (elapsedTime >= API_CONFIG.POLLING.TIMEOUT) {
        clearInterval(pollInterval);
        reject({
          isPollingTimeout: true,
          message: 'Processing timeout after 2 minutes',
        });
        return;
      }

      try {
        const response = await axios.get(
          buildExternalAiUrl(`${API_CONFIG.ENDPOINTS.STATUS}/${jobId}`),
          {
            headers: externalAiHeaders(),
            timeout: 10000, // 10 second timeout for status check
          }
        );

        const { status, galleryUrl, error, data } = response.data;

        // Notify status change
        if (onStatusChange) {
          onStatusChange({
            status,
            attempts,
            elapsedTime,
          });
        }

        // Check for final states
        if (status === 'completed' || status === 'success') {
          clearInterval(pollInterval);
          resolve({
            status: 'completed',
            galleryUrl,
            data,
          });
        } else if (status === 'failed' || status === 'error') {
          clearInterval(pollInterval);
          reject({
            isJobFailed: true,
            message: error || 'Processing failed',
            status: 'failed',
          });
        }
        // Continue polling for: 'queued', 'processing', 'pending', etc.

      } catch (error) {
        // Don't stop polling on network errors, just log
        console.error('Status check error:', error);
        
        // But stop if it's a 404 (job not found) or 401 (auth error)
        if (error.response?.status === 404 || error.response?.status === 401) {
          clearInterval(pollInterval);
          reject(error);
        }
      }
    };

    // Start polling
    pollInterval = setInterval(checkStatus, API_CONFIG.POLLING.INTERVAL);
    
    // Check immediately
    checkStatus();
  });
};

/**
 * Cancel polling (for cleanup)
 * Note: This is handled by clearing interval in pollJobStatus
 * This function is for external cancellation if needed
 */
export const createCancellablePoller = (jobId, onStatusChange = null) => {
  let isCancelled = false;
  let pollInterval = null;

  const cancel = () => {
    isCancelled = true;
    if (pollInterval) {
      clearInterval(pollInterval);
    }
  };

  const promise = new Promise((resolve, reject) => {
    let attempts = 0;
    const startTime = Date.now();

    const checkStatus = async () => {
      if (isCancelled) {
        clearInterval(pollInterval);
        reject({ isCancelled: true, message: 'Polling cancelled' });
        return;
      }

      attempts++;
      const elapsedTime = Date.now() - startTime;

      if (elapsedTime >= API_CONFIG.POLLING.TIMEOUT) {
        clearInterval(pollInterval);
        reject({
          isPollingTimeout: true,
          message: 'Processing timeout after 2 minutes',
        });
        return;
      }

      try {
        const response = await axios.get(
          buildExternalAiUrl(`${API_CONFIG.ENDPOINTS.STATUS}/${jobId}`),
          {
            headers: externalAiHeaders(),
            timeout: 10000,
          }
        );

        const { status, galleryUrl, error, data } = response.data;

        if (onStatusChange) {
          onStatusChange({
            status,
            attempts,
            elapsedTime,
          });
        }

        if (status === 'completed' || status === 'success') {
          clearInterval(pollInterval);
          resolve({
            status: 'completed',
            galleryUrl,
            data,
          });
        } else if (status === 'failed' || status === 'error') {
          clearInterval(pollInterval);
          reject({
            isJobFailed: true,
            message: error || 'Processing failed',
            status: 'failed',
          });
        }

      } catch (error) {
        console.error('Status check error:', error);
        
        if (error.response?.status === 404 || error.response?.status === 401) {
          clearInterval(pollInterval);
          reject(error);
        }
      }
    };

    pollInterval = setInterval(checkStatus, API_CONFIG.POLLING.INTERVAL);
    checkStatus();
  });

  return { promise, cancel };
};

export default pollJobStatus;


